import React from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const defaultPlaceholder =
  'Search candidates with natural language (e.g., candidates with React and 5+ years experience)...';

/**
 * Controlled AI candidate search bar: input, search button, clear button.
 * Parent owns state and passes value, onChange, onSearch, onClear, and loading/active flags.
 */
function AICandidateSearchBar({
  value,
  onChange,
  onSearch,
  onClear,
  isSearching = false,
  isActive = false,
  placeholder = defaultPlaceholder,
  disabled = false,
  searchButtonLabel = 'Search',
  className = '',
  rightActions = null,
}) {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch(value);
    }
  };

  const handleClear = () => {
    onChange('');
    onClear();
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1 relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="w-full pl-4 pr-28 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF]"
          disabled={disabled || isSearching}
          aria-label="AI candidate search"
        />
        <button
          type="button"
          onClick={() => onSearch(value)}
          disabled={disabled || isSearching || !(value && value.trim())}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-2 bg-[#007BFF] text-white text-sm font-medium rounded-md hover:bg-[#0062CC] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 inline-flex items-center gap-1.5"
        >
          {isSearching ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Searching</span>
            </>
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4" />
          )}
        </button>
      </div>
      {rightActions}
      {isActive && (
        <button
          type="button"
          onClick={handleClear}
          className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default AICandidateSearchBar;
